import { c as _c } from "react/compiler-runtime";
import type { ColumnFiltersState, ColumnVisibilityState, SortingState } from '@tanstack/table-core';
import { isEqual } from 'lodash-es';
import { type SetStateAction, useCallback, useEffect, useState } from 'react';
import { isAddress, isAddressEqual } from 'viem';
import type { PageKey } from '../../types';
import type { EditableTableSettingsState, TableSettingsState } from '../shared';
import { getTableSettingsFromCookie, setTableSettingsInCookie } from './cookieStorage';

/**
 * Compares two filter-owner keys. EVM addresses are case-insensitive, so a
 * checksummed key (e.g. a connected-wallet address, or the checksummed
 * `/dashboard/:address` URL) and the same address lowercased must count as the
 * same owner; non-address keys fall back to strict equality.
 */
const isSameFiltersOwner = (a: string, b: string) => isAddress(a, {
  strict: false
}) && isAddress(b, {
  strict: false
}) ? isAddressEqual(a, b) : a === b;

/**
 * Whether the persisted filters belong to a different owner than the current
 * `resetFiltersKey` and must be dropped. Empty filters never need a reset; a
 * missing owner is treated as a mismatch so filters persisted before this
 * scoping existed (or set without a key) don't leak across wallets.
 */
const shouldDropPersistedFilters = (persisted: TableSettingsState, resetFiltersKey: string | undefined) => {
  if (resetFiltersKey === undefined || persisted.columnFilters.length === 0) return false;
  if (persisted.filtersOwnerKey === undefined) return true;
  return !isSameFiltersOwner(persisted.filtersOwnerKey, resetFiltersKey);
};

// Drop another wallet's filters so a wallet switch (which remounts this hook)
// never flashes the previous wallet's filters. The cookie itself is reconciled
// by the owner effect below.
const withOwnedFilters = (persisted: TableSettingsState, defaultTableSettings: TableSettingsState, resetFiltersKey: string | undefined): TableSettingsState => shouldDropPersistedFilters(persisted, resetFiltersKey) ? {
  ...persisted,
  columnFilters: defaultTableSettings.columnFilters,
  filtersOwnerKey: resetFiltersKey
} : persisted;
export interface TableSettingsProps {
  pageKey: PageKey;
  tableSettingsObjectKey: string;
  defaultTableSettings: TableSettingsState;
  /**
   * Scopes persisted column filters to this key (e.g. the viewed wallet
   * address). The key the filters were saved under is persisted in the cookie
   * alongside them, so a page refresh (same key) keeps the filters while
   * viewing a different wallet drops them — even though navigating between
   * `/dashboard/[address]` values remounts this hook, which a ref couldn't
   * survive.
   */
  resetFiltersKey?: string;
  /**
   * Settings already read from the request cookie by the server component that
   * renders this table (`safeGetTableSettings`). When provided, the first
   * render uses them on both server and client, so the hydrated table matches
   * the server HTML and the persisted sorting matches the prefetched query.
   * When omitted, the first render uses `defaultTableSettings`. Either way the
   * browser cookie is reconciled right after mount, since a cached RSC payload
   * (e.g. browser Back) can carry settings older than the cookie.
   */
  initialTableSettings?: TableSettingsState;
}

/**
 * Hook that manages table settings with cookie persistence.
 * Handles column visibility, filters, sorting, and ordering with automatic cookie sync.
 * @param params - Configuration object
 * @param params.pageKey - Page identifier for cookie storage
 * @param params.tableSettingsObjectKey - Table identifier within page
 * @param params.defaultTableSettings - Default settings for fallback
 * @param params.resetFiltersKey - Scopes persisted filters to this key (e.g. the viewed wallet); filters are dropped when a different key is viewed
 * @param params.initialTableSettings - Settings read from the request cookie on the server, so the first client render matches the server HTML
 * @returns Table settings state and setter functions
 */
const useTableSettings = t0 => {
  const $ = _c(43);
  const {
    pageKey,
    tableSettingsObjectKey,
    defaultTableSettings,
    resetFiltersKey,
    initialTableSettings
  } = t0;
  let t1;
  if ($[0] !== defaultTableSettings || $[1] !== initialTableSettings || $[2] !== resetFiltersKey) {
    t1 = () => withOwnedFilters(initialTableSettings ?? defaultTableSettings, defaultTableSettings, resetFiltersKey);
    $[0] = defaultTableSettings;
    $[1] = initialTableSettings;
    $[2] = resetFiltersKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [store, setStore] = useState(t1);
  let t2;
  let t3;
  if ($[4] !== defaultTableSettings || $[5] !== pageKey || $[6] !== resetFiltersKey || $[7] !== tableSettingsObjectKey) {
    t2 = () => {
      const persisted = withOwnedFilters(getTableSettingsFromCookie({
        pageKey,
        tableSettingsObjectKey,
        defaultTableSettings
      }), defaultTableSettings, resetFiltersKey);
      setStore(current => isEqual(current, persisted) ? current : persisted);
    };
    t3 = [pageKey, tableSettingsObjectKey, defaultTableSettings, resetFiltersKey];
    $[4] = defaultTableSettings;
    $[5] = pageKey;
    $[6] = resetFiltersKey;
    $[7] = tableSettingsObjectKey;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== defaultTableSettings || $[11] !== pageKey || $[12] !== tableSettingsObjectKey) {
    t4 = (key, value) => {
      setStore(prevStore => {
        const valueToStore = value instanceof Function ? value(prevStore[key]) : value;
        setTableSettingsInCookie({
          pageKey,
          tableSettingsObjectKey,
          defaultTableSettings,
          key,
          valueToStore
        });
        const newState = {
          ...prevStore,
          [key]: valueToStore
        };
        return newState;
      });
    };
    $[10] = defaultTableSettings;
    $[11] = pageKey;
    $[12] = tableSettingsObjectKey;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const _set = t4;
  let t5;
  if ($[14] !== _set) {
    t5 = columnVisibility => {
      _set("columnVisibility", columnVisibility);
    };
    $[14] = _set;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const setColumnVisibility = t5;
  let t6;
  if ($[16] !== _set) {
    t6 = sorting => {
      _set("sorting", sorting);
    };
    $[16] = _set;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const setSorting = t6;
  let t7;
  if ($[18] !== _set || $[19] !== resetFiltersKey) {
    t7 = columnFilters => {
      _set("columnFilters", columnFilters);
      if (resetFiltersKey !== undefined) {
        _set("filtersOwnerKey", resetFiltersKey);
      }
    };
    $[18] = _set;
    $[19] = resetFiltersKey;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const setColumnFilters = t7;
  let t8;
  if ($[21] !== _set) {
    t8 = columnOrder => {
      _set("columnOrder", columnOrder);
    };
    $[21] = _set;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const setColumnOrder = t8;
  let t9;
  if ($[23] !== defaultTableSettings.sorting || $[24] !== setSorting) {
    t9 = () => {
      setSorting(defaultTableSettings.sorting);
    };
    $[23] = defaultTableSettings.sorting;
    $[24] = setSorting;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const resetSorting = t9;
  let t10;
  let t11;
  if ($[26] !== _set || $[27] !== defaultTableSettings || $[28] !== pageKey || $[29] !== resetFiltersKey || $[30] !== tableSettingsObjectKey) {
    t10 = () => {
      const persisted_0 = getTableSettingsFromCookie({
        pageKey,
        tableSettingsObjectKey,
        defaultTableSettings
      });
      if (!shouldDropPersistedFilters(persisted_0, resetFiltersKey)) {
        return;
      }
      _set("columnFilters", defaultTableSettings.columnFilters);
      _set("filtersOwnerKey", resetFiltersKey);
    };
    t11 = [resetFiltersKey, _set, pageKey, tableSettingsObjectKey, defaultTableSettings];
    $[26] = _set;
    $[27] = defaultTableSettings;
    $[28] = pageKey;
    $[29] = resetFiltersKey;
    $[30] = tableSettingsObjectKey;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  if ($[33] !== resetSorting || $[34] !== setColumnFilters || $[35] !== setColumnOrder || $[36] !== setColumnVisibility || $[37] !== setSorting || $[38] !== store.columnFilters || $[39] !== store.columnOrder || $[40] !== store.columnVisibility || $[41] !== store.sorting) {
    t12 = {
      columnVisibility: store.columnVisibility,
      columnFilters: store.columnFilters,
      columnOrder: store.columnOrder,
      sorting: store.sorting,
      setColumnFilters,
      setColumnVisibility,
      setSorting,
      resetSorting,
      setColumnOrder
    };
    $[33] = resetSorting;
    $[34] = setColumnFilters;
    $[35] = setColumnOrder;
    $[36] = setColumnVisibility;
    $[37] = setSorting;
    $[38] = store.columnFilters;
    $[39] = store.columnOrder;
    $[40] = store.columnVisibility;
    $[41] = store.sorting;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
};
export default useTableSettings;